// Fiche d'un dossier : onglets Instruction (pièces et verdicts, demande de complément, actions),
// Formulaire (données rendues selon le schéma de la version), Documents, Conversation usager
// (fond distinct des notes internes), Historique ; colonne de droite : situation et délais,
// décision avec document généré, notes internes.
import { LIBELLES_ETATS_DOSSIER, type DossierDetail, type EvenementHistorique, type TransitionDisponible } from '@cc/contrats/ui-metier';
import { LienBouton, Onglets, Panneau } from '@cc/ui';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useParams, useSearchParams } from 'react-router';
import { requete } from '../../api/client';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { ActionMetier } from './ActionMetier';
import { ConversationUsager } from './dossier/ConversationUsager';
import { DecisionDossier } from './dossier/DecisionDossier';
import { DemandeComplement } from './dossier/DemandeComplement';
import { DonneesFormulaireDossier } from './dossier/DonneesFormulaireDossier';
import { EcheancesDossier } from './dossier/EcheancesDossier';
import { IdentiteNipDossier } from './dossier/IdentiteNipDossier';
import { NotesInternes } from './dossier/NotesInternes';
import { PiecesDossier } from './dossier/PiecesDossier';
import { Historique, Pieces } from './Fiches';
import { useServices } from '../demarches/contenu';
import { etapesDeRetour, libelleEtatDossier } from './listes';
import commun from '../../commun/commun.module.css';
import styles from './metier.module.css';

function ActionsDossier({ dossier: d, transitions, invalider }: { dossier: DossierDetail; transitions: TransitionDisponible[]; invalider: (readonly unknown[])[] }) {
  const { contexte } = useSession();
  const services = useServices();
  const terminal = ['cloture', 'classe_sans_suite', 'archive', 'retire'].includes(d.etat);
  // Même clé et même forme que l'onglet Historique : une seule requête, rafraîchie après chaque action.
  const cheminHistorique = `/dossiers/${d.id}/historique`;
  const historique = useQuery({
    queryKey: [cheminHistorique],
    queryFn: ({ signal }) => requete<{ elements: EvenementHistorique[] }>(cheminHistorique, { signal }).then((r) => r.elements),
    retry: false,
  });
  const retours = etapesDeRetour(historique.data ?? [], d.etat);
  const autres = transitions.filter((t) => t.code !== 'decider' && t.code !== 'demander_complement');
  const complement = transitions.find((t) => t.code === 'demander_complement');
  return (
    <div className={styles.actions}>
      {complement ? <DemandeComplement dossier={d} transition={complement} invalider={invalider} /> : null}
      {autres.map((t) => (
        <ActionMetier
          key={t.code}
          libelle={t.libelle}
          titre={t.libelle}
          explication={`Le dossier passera à l'état « ${libelleEtatDossier(t.vers)} ».`}
          chemin={`/dossiers/${d.id}/transitions/${t.code}`}
          champs={
            t.code === 'transmettre'
              ? [{ nom: 'service_id', libelle: 'Service destinataire', obligatoire: true, options: (services.data ?? []).map((s) => ({ valeur: s.id, libelle: s.nom })) }, { nom: 'motif', libelle: 'Motif', multiligne: true }]
              : [{ nom: 'motif', libelle: 'Motif', obligatoire: t.motif_obligatoire, multiligne: true }]
          }
          succes="Transition enregistrée."
          invalider={invalider}
          variante={['cloturer', 'notifier', 'signer', 'etape_suivante', 'enregistrer'].includes(t.code) ? 'principal' : 'secondaire'}
        />
      ))}
      {!terminal && peut(contexte, 'dossier', 'modifier') && retours.length > 0 ? (
        <ActionMetier
          libelle="Retour motivé"
          titre="Retour motivé"
          explication="Le dossier revient à une étape déjà franchie ; le motif est conservé dans l'historique."
          chemin={`/dossiers/${d.id}/retour`}
          champs={[
            { nom: 'vers', libelle: 'Étape de retour', obligatoire: true, options: retours.map((e) => ({ valeur: e, libelle: libelleEtatDossier(e) })) },
            { nom: 'motif', libelle: 'Motif du retour', obligatoire: true, multiligne: true },
          ]}
          succes="Retour enregistré."
          invalider={invalider}
          variante="secondaire"
        />
      ) : null}
      {terminal && d.etat !== 'retire' && peut(contexte, 'dossier', 'modifier') ? (
        <ActionMetier libelle="Rouvrir" titre="Rouvrir le dossier" explication="Le dossier repart en instruction ; l'ancienne décision reste visible et le compteur de réouvertures augmente." chemin={`/dossiers/${d.id}/reouvrir`} champs={[{ nom: 'motif', libelle: 'Motif de la réouverture', obligatoire: true, multiligne: true }]} succes="Dossier rouvert." invalider={invalider} variante="secondaire" />
      ) : null}
      {transitions.length === 0 && !peut(contexte, 'dossier', 'modifier') ? <p className={commun.na}>Aucune action ne vous est ouverte sur ce dossier.</p> : null}
    </div>
  );
}

export function PageDossier() {
  const { id = '' } = useParams();
  const { contexte } = useSession();
  const [params, setParams] = useSearchParams();
  const dossier = useQuery({
    queryKey: ['dossiers', 'fiche', id],
    queryFn: ({ signal }) => requete<DossierDetail>(`/dossiers/${id}`, { signal }),
    retry: false,
  });
  const d = dossier.data;
  const invalider = [['dossiers'], [`/dossiers/${id}/historique`], ['documents', 'objet', 'dossier', id], ['indicateurs']];
  const transitions: TransitionDisponible[] = d?.transitions_possibles ?? [];
  const peutModifier = peut(contexte, 'dossier', 'modifier');
  const onglet = params.get('onglet') ?? 'instruction';

  return (
    <>
      <EnTetePage
        etiquette={d ? `${d.demarche.libelle}, version ${d.demarche.numero_version}${d.service ? `, ${d.service.nom}` : ''}` : 'Dossier'}
        titre={d ? `Dossier ${d.numero ?? 'sans numéro'}` : 'Dossier'}
        description={
          d ? (
            <span>
              Demandeur : {d.demandeur.nom ?? (d.demandeur.usager_compte_id ? 'usager inscrit' : 'non renseigné')}
              {d.enregistre_le ? `, déposé le ${date(d.enregistre_le)} (${d.canal_depot})` : ''}
              {d.arrondissement ? `, ${d.arrondissement.nom}` : ''}.
            </span>
          ) : null
        }
        actions={
          <LienBouton href="/dossiers" icone={ArrowLeft}>
            Dossiers
          </LienBouton>
        }
      />
      <EtatRequete chargement={dossier.isPending} erreur={dossier.error} onReessayer={() => void dossier.refetch()} lot="L0-C" fonction="La fiche du dossier">
        {d ? (
          <div className={commun.deuxColonnes}>
            <div className={styles.pile}>
              <Onglets
                libelle="Contenu du dossier"
                actif={onglet}
                onChanger={(o) => setParams((p) => { p.set('onglet', o); return p; }, { replace: true })}
                onglets={[
                  {
                    id: 'instruction',
                    libelle: 'Instruction',
                    contenu: (
                      <div className={styles.pile}>
                        <PiecesDossier dossier={d} peutModifier={peutModifier} />
                        <Panneau titre="Actions" niveau={3}>
                          <ActionsDossier dossier={d} transitions={transitions} invalider={invalider} />
                        </Panneau>
                      </div>
                    ),
                  },
                  { id: 'formulaire', libelle: 'Formulaire', contenu: <DonneesFormulaireDossier dossier={d} /> },
                  { id: 'documents', libelle: 'Documents', contenu: <Pieces objetType="dossier" objetId={d.id} peutDeposer={peutModifier} /> },
                  { id: 'conversation', libelle: 'Conversation usager', contenu: <ConversationUsager dossierId={d.id} peutRepondre={peutModifier} /> },
                  { id: 'historique', libelle: 'Historique', contenu: <Historique chemin={`/dossiers/${d.id}/historique`} libelles={LIBELLES_ETATS_DOSSIER} /> },
                ]}
              />
            </div>
            <div className={styles.pile}>
              <EcheancesDossier dossier={d} invalider={invalider} />
              <DecisionDossier dossier={d} transition={transitions.find((t) => t.code === 'decider')} invalider={invalider} />
              {peut(contexte, 'identite_nip', 'voir') ? <IdentiteNipDossier dossierId={d.id} peutVerifier={peut(contexte, 'identite_nip', 'modifier')} peutInstruire={peut(contexte, 'identite_nip', 'valider')} /> : null}
              <NotesInternes dossierId={d.id} peutAjouter={peutModifier} />
            </div>
          </div>
        ) : null}
      </EtatRequete>
    </>
  );
}
